/**
 * Free/public email providers.
 *
 * Two callers with opposite jobs share this list, which is why it lives here
 * rather than beside either of them. The feasibility resolver refuses to
 * attribute one of these to an organization, because a stranger's personal
 * inbox is not evidence of where they work. The researcher booking form
 * refuses one at the door, for the same reason read forwards: an address here
 * tells us nothing about the company, and every junk organization we have
 * traced came in behind one.
 *
 * Deliberately short and hand-kept. It is the set worth refusing outright, not
 * an attempt to enumerate consumer mail, and a domain only earns a place once
 * it shows up in real signups. `isCompanyEmailDomain` is the safer default for
 * anything that gates a person: unknown means allowed.
 */
const PUBLIC_MAILBOX_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "yahoo.co.uk",
  "icloud.com",
  "mail.com",
  "me.com",
  "aol.com",
  "proton.me",
  "gmx.com",
]);

/** True for a free/public email provider. Takes a bare domain, not an address. */
export function isPublicMailboxDomain(domain: string): boolean {
  return PUBLIC_MAILBOX_DOMAINS.has(domain.trim().toLowerCase());
}

/**
 * True when an address looks like it belongs to a company rather than a
 * personal mailbox.
 *
 * Anything unparseable is treated as a company address on purpose: this gates
 * a form, and the cost of turning away a real buyer over a parsing edge case is
 * higher than the cost of letting one odd address through to a human. The
 * required `type="email"` is what rejects a malformed address; this only
 * answers the narrower question.
 */
export function isCompanyEmailDomain(email: string): boolean {
  const domain = email.trim().toLowerCase().split("@")[1];
  if (!domain) {
    return true;
  }
  return !isPublicMailboxDomain(domain);
}
